import {
  analyze,
  createLineIndex,
  Lexer,
  type QueryStatement,
  type Statement,
  type Token,
} from "@saralsql/tsql-parser";

export type CodeFormatErrorRange = {
  readonly from: number;
  readonly to: number;
};

/** Mirrors `@saralsql/tsql-parser` TokenType; the package only type-exports it. */
const TokenKind = {
  Keyword: 0,
  Identifier: 1,
  Variable: 2,
  Operator: 4,
  Number: 5,
  OpenParen: 7,
  CloseParen: 8,
  Semicolon: 9,
  EOF: 10,
  Comma: 11,
  Dot: 12,
} as const;

const DUPLICATE_KEYWORDS = new Set([
  "SELECT",
  "DISTINCT",
  "AND",
  "ORDER",
  "UNION",
  "ALL",
]);
const DANGLING_AND_FOLLOWERS = new Set(["GROUP", "ORDER", "HAVING", "FROM"]);
const PARSER_CLAUSE_NOISE = new Set([
  "WHERE",
  "AND",
  "FROM",
  "SELECT",
  "OR",
  "WHEN",
  "JOIN",
]);
const SKIP_STATEMENT_TYPES = new Set([
  "ErrorStatement",
  "BatchSeparatorStatement",
]);

export type SqlAnalysis = {
  readonly statements: readonly Statement[];
  readonly errors: readonly CodeFormatErrorRange[];
};

function toParserSql(source: string): string {
  return source.replaceAll(/(^|[^:]):([A-Za-z_]\w*)/g, "$1@$2");
}

function isKind(token: Token | undefined, kind: number): boolean {
  return token?.type === kind;
}

function isKeyword(token: Token | undefined, word: string): boolean {
  return isKind(token, TokenKind.Keyword) && token?.value === word;
}

function tokenEnd(token: Token): number {
  return token.offset + Math.max(token.raw?.length ?? token.value.length, 1);
}

function tokenRange(token: Token): CodeFormatErrorRange {
  return { from: token.offset, to: tokenEnd(token) };
}

function addToken(
  errors: CodeFormatErrorRange[],
  token: Token | undefined,
): void {
  if (token !== undefined) {
    errors.push(tokenRange(token));
  }
}

function lex(source: string): Token[] {
  const lexer = new Lexer(source);
  const tokens: Token[] = [];
  while (true) {
    const token = lexer.nextToken();
    if (token.type === TokenKind.EOF) {
      return tokens;
    }
    tokens.push(token);
  }
}

function qualifiedNameEnd(
  tokens: readonly Token[],
  start: number,
): number | null {
  if (
    !isKind(tokens[start], TokenKind.Identifier) ||
    !isKind(tokens[start + 1], TokenKind.Dot) ||
    !isKind(tokens[start + 2], TokenKind.Identifier)
  ) {
    return null;
  }
  return start + 2;
}

function missingSeparatorAfterQualifiedName(
  tokens: readonly Token[],
  qualifiedEnd: number,
): CodeFormatErrorRange | null {
  const firstEnd = tokens[qualifiedEnd];
  const start = tokens[qualifiedEnd + 1];
  if (
    firstEnd === undefined ||
    start === undefined ||
    isKind(start, TokenKind.Dot) ||
    start.offset <= tokenEnd(firstEnd)
  ) {
    return null;
  }
  const following = qualifiedNameEnd(tokens, qualifiedEnd + 1);
  const end = following === null ? undefined : tokens[following];
  if (end === undefined) {
    return null;
  }
  return { from: start.offset, to: tokenEnd(end) };
}

function trimRange(
  source: string,
  from: number,
  to: number,
): CodeFormatErrorRange | null {
  let start = from;
  let end = Math.min(to, source.length);
  while (start < end && /\s/.test(source[start] ?? "")) {
    start += 1;
  }
  while (end > start && /\s/.test(source[end - 1] ?? "")) {
    end -= 1;
  }
  return end > start ? { from: start, to: end } : null;
}

function mergeRanges(
  ranges: readonly CodeFormatErrorRange[],
): CodeFormatErrorRange[] {
  const merged: CodeFormatErrorRange[] = [];
  for (const range of [...ranges].sort(
    (left, right) => left.from - right.from,
  )) {
    const previous = merged.at(-1);
    if (previous && range.from <= previous.to) {
      merged[merged.length - 1] = {
        from: previous.from,
        to: Math.max(previous.to, range.to),
      };
    } else {
      merged.push(range);
    }
  }
  return merged;
}

function parserErrors(
  source: string,
  analysis: ReturnType<typeof analyze>,
): CodeFormatErrorRange[] {
  const ranges: CodeFormatErrorRange[] = [];
  for (const diagnostic of analysis.diagnostics) {
    if (diagnostic.source !== "parser") {
      continue;
    }
    const from = diagnostic.start;
    const to = Math.max(diagnostic.end, from);
    const text = source.slice(from, to).trim();
    const qualified =
      /^[A-Za-z_]\w*\.$/.test(text) ||
      (source[to] === "." && /^[A-Za-z_]\w*$/.test(text));
    if (
      text.length === 0 ||
      to - from > 40 ||
      qualified ||
      PARSER_CLAUSE_NOISE.has(text.toUpperCase())
    ) {
      continue;
    }
    const range = trimRange(source, from, to);
    if (range) {
      ranges.push(range);
    }
  }
  return ranges;
}

function extraStatementErrors(
  source: string,
  statements: readonly Statement[],
): CodeFormatErrorRange[] {
  const extra = statements[1];
  if (statements.length <= 1 || extra === undefined) {
    return [];
  }
  const lineEnd = source.indexOf("\n", extra.start);
  const to =
    lineEnd === -1
      ? extra.end
      : Math.min(extra.end, Math.max(lineEnd, extra.start + 1));
  return [{ from: extra.start, to: Math.max(to, extra.start + 1) }];
}

function executableStatements(statements: readonly Statement[]): Statement[] {
  return statements.filter(
    (statement) => !SKIP_STATEMENT_TYPES.has(statement.type),
  );
}

type RecoveryScan = {
  readonly tokens: readonly Token[];
  readonly index: number;
  readonly prev: Token | undefined;
  readonly token: Token;
  readonly next: Token | undefined;
  readonly after: Token | undefined;
  readonly parens: Token[];
  readonly cases: Token[];
  readonly fromSeenAtDepth: Set<number>;
  readonly errors: CodeFormatErrorRange[];
};

function trackParens(scan: RecoveryScan): void {
  const { token, parens, errors } = scan;
  if (isKind(token, TokenKind.OpenParen)) {
    parens.push(token);
  }
  if (!isKind(token, TokenKind.CloseParen)) {
    return;
  }
  if (parens.length === 0) {
    addToken(errors, token);
    return;
  }
  parens.pop();
}

function trackCases(scan: RecoveryScan): void {
  const { token, cases } = scan;
  if (isKeyword(token, "CASE")) {
    cases.push(token);
    return;
  }
  if (isKeyword(token, "END") && cases.length > 0) {
    cases.pop();
  }
}

function trackSelectFrom(scan: RecoveryScan): void {
  const { token, parens, fromSeenAtDepth, errors } = scan;
  const depth = parens.length;
  if (isKeyword(token, "SELECT")) {
    fromSeenAtDepth.delete(depth);
  }
  if (!isKeyword(token, "FROM")) {
    return;
  }
  if (fromSeenAtDepth.has(depth)) {
    addToken(errors, token);
    return;
  }
  fromSeenAtDepth.add(depth);
}

function flagAdjacentPunctuation(scan: RecoveryScan): void {
  const { token, next, errors } = scan;
  if (isKind(token, TokenKind.Comma) && isKind(next, TokenKind.Comma)) {
    addToken(errors, next);
  }
  if (isKind(token, TokenKind.Variable) && isKind(next, TokenKind.Variable)) {
    addToken(errors, next);
  }
  if (isKind(token, TokenKind.Number) && isKind(next, TokenKind.Variable)) {
    addToken(errors, next);
  }
  if (next === undefined) {
    return;
  }
  if (
    isKind(token, TokenKind.Operator) &&
    token.value === "=" &&
    isKind(next, TokenKind.Operator) &&
    next.value === "="
  ) {
    errors.push({ from: token.offset, to: tokenEnd(next) });
  }
}

function isAsAnd(prev: Token | undefined, token: Token): boolean {
  return token.value === "AND" && isKeyword(prev, "AS");
}

function flagDuplicateKeywords(scan: RecoveryScan): void {
  const { prev, token, next, after, errors } = scan;
  if (
    isKind(token, TokenKind.Keyword) &&
    isKind(next, TokenKind.Keyword) &&
    token.value === next?.value &&
    DUPLICATE_KEYWORDS.has(token.value) &&
    !isAsAnd(prev, token)
  ) {
    addToken(errors, next);
  }
  if (
    isKeyword(token, "SELECT") &&
    isKeyword(next, "DISTINCT") &&
    isKeyword(after, "SELECT")
  ) {
    addToken(errors, after);
  }
}

function flagKeywordShapes(scan: RecoveryScan): void {
  const { prev, token, next, after, errors } = scan;
  if (isKeyword(token, "AS") && isKeyword(next, "AND")) {
    addToken(errors, token);
  }
  if (isKeyword(token, "CAST") && !isKind(next, TokenKind.OpenParen)) {
    addToken(errors, token);
  }
  if (
    isKind(token, TokenKind.Identifier) &&
    token.value.toUpperCase() === "FORM" &&
    isKind(next, TokenKind.Identifier)
  ) {
    addToken(errors, token);
  }
  if (
    isKind(token, TokenKind.Dot) &&
    token.value === "::" &&
    isKind(prev, TokenKind.Operator) &&
    isKind(next, TokenKind.Identifier) &&
    !isKind(after, TokenKind.OpenParen)
  ) {
    addToken(errors, token);
  }
}

function flagDanglingAnd(scan: RecoveryScan): void {
  const { token, next, errors } = scan;
  if (!isKeyword(token, "AND")) {
    return;
  }
  if (
    next === undefined ||
    isKind(next, TokenKind.CloseParen) ||
    isKind(next, TokenKind.Semicolon) ||
    (isKind(next, TokenKind.Keyword) &&
      DANGLING_AND_FOLLOWERS.has(next?.value ?? ""))
  ) {
    addToken(errors, token);
  }
}

function flagQualifiedNameGaps(scan: RecoveryScan): void {
  const { tokens, index, token, next, errors } = scan;
  const qualifiedEnd = qualifiedNameEnd(tokens, index);
  if (qualifiedEnd !== null) {
    const range = missingSeparatorAfterQualifiedName(tokens, qualifiedEnd);
    if (range !== null) {
      errors.push(range);
    }
  }
  if (!isKind(token, TokenKind.Variable)) {
    return;
  }
  const following = qualifiedNameEnd(tokens, index + 1);
  const end = following === null ? undefined : tokens[following];
  if (next === undefined || end === undefined) {
    return;
  }
  errors.push({ from: next.offset, to: tokenEnd(end) });
}

function flagExtraStatementAfterSemicolon(scan: RecoveryScan): void {
  const { tokens, index, token, errors } = scan;
  if (!isKind(token, TokenKind.Semicolon)) {
    return;
  }
  let extraIndex = index + 1;
  while (isKind(tokens[extraIndex], TokenKind.CloseParen)) {
    extraIndex += 1;
  }
  const extra = tokens[extraIndex];
  if (
    isKeyword(extra, "HAVING") ||
    isKeyword(extra, "SELECT") ||
    isKeyword(extra, "WITH")
  ) {
    addToken(errors, extra);
  }
}

function scanRecoveryToken(scan: RecoveryScan): void {
  trackParens(scan);
  trackCases(scan);
  trackSelectFrom(scan);
  flagAdjacentPunctuation(scan);
  flagDuplicateKeywords(scan);
  flagKeywordShapes(scan);
  flagDanglingAnd(scan);
  flagQualifiedNameGaps(scan);
  flagExtraStatementAfterSemicolon(scan);
}

/** Token rules for recovery holes SaralSQL's parser does not report. */
function strictSyntaxErrors(source: string): CodeFormatErrorRange[] {
  const tokens = lex(source);
  const errors: CodeFormatErrorRange[] = [];
  const parens: Token[] = [];
  const cases: Token[] = [];
  const fromSeenAtDepth = new Set<number>();

  for (let index = 0; index < tokens.length; index += 1) {
    const token = tokens[index];
    if (token === undefined) {
      continue;
    }
    scanRecoveryToken({
      tokens,
      index,
      prev: tokens[index - 1],
      token,
      next: tokens[index + 1],
      after: tokens[index + 2],
      parens,
      cases,
      fromSeenAtDepth,
      errors,
    });
  }

  errors.push(...parens.map(tokenRange), ...cases.map(tokenRange));
  return errors;
}

export function isReadOnly(statement: Statement | QueryStatement): boolean {
  switch (statement.type) {
    case "SelectStatement":
      return statement.into === undefined;
    case "SetOperator":
      return isReadOnly(statement.left) && isReadOnly(statement.right);
    case "WithStatement":
      return isReadOnly(statement.body);
    default:
      return false;
  }
}

export function analyzeSql(source: string): SqlAnalysis {
  const parserSource = toParserSql(source);
  const analysis = analyze(parserSource);
  const statements = executableStatements(analysis.ast.body);
  return {
    statements,
    errors: mergeRanges([
      ...parserErrors(source, analysis),
      ...strictSyntaxErrors(parserSource),
      ...extraStatementErrors(source, statements),
    ]),
  };
}

export function formatErrorLines(
  source: string,
  ranges: readonly CodeFormatErrorRange[],
): number[] {
  const index = createLineIndex(source);
  const sourceLines = source.split("\n");
  return [
    ...new Set(
      ranges
        .map((range) => index.offsetToPosition(range.from).line + 1)
        .filter((line) => (sourceLines[line - 1]?.length ?? 0) <= 80),
    ),
  ].sort((left, right) => left - right);
}

/** Used by golden tests to assert highlighted SQL spans. */
export function inspectSqlFormatErrors(source: string): {
  lines: readonly number[];
  ranges: readonly CodeFormatErrorRange[];
} {
  const ranges = analyzeSql(source).errors;
  return { lines: formatErrorLines(source, ranges), ranges };
}
